import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { Iconify } from 'src/components';

export default function YearOverYearChart({ data, title }) {
    const formatValue = (value, metric) => {
        if (metric === 'Revenue') {
            if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
            if (value >= 1000) return `£${(value / 1000).toFixed(0)}K`;
            return `£${value}`;
        }
        return value?.toLocaleString() || 0;
    };

    // Find max value for scaling bars
    const maxValue = Math.max(
        ...data.flatMap((d) => [d.current, d.previous]),
        1
    );

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                mb: 3,
            }}
        >
            <Typography sx={{ fontSize: '20px', fontWeight: 600, mb: 3 }}>
                {title}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {data.map((item, index) => {
                    const currentWidth = (item.current / maxValue) * 100;
                    const previousWidth = (item.previous / maxValue) * 100;
                    const isPositive = item.change >= 0;

                    return (
                        <Box key={item.metric || index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {/* Label */}
                            <Typography
                                sx={{
                                    width: '120px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    flexShrink: 0,
                                }}
                            >
                                {item.metric}
                            </Typography>

                            {/* Bars container */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {/* Current period bar */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            height: '20px',
                                            width: `${Math.max(currentWidth, 5)}%`,
                                            borderRadius: '4px',
                                            bgcolor: '#3b82f6',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            px: 1,
                                            transition: 'width 0.5s ease',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                color: '#fff',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {formatValue(item.current, item.metric)}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: '#64748b',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Current
                                    </Typography>
                                </Box>

                                {/* Previous period bar */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            height: '20px',
                                            width: `${Math.max(previousWidth, 5)}%`,
                                            borderRadius: '4px',
                                            bgcolor: '#94a3b8',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            px: 1,
                                            transition: 'width 0.5s ease',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                color: '#fff',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {formatValue(item.previous, item.metric)}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: '#64748b',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Previous
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Change indicator */}
                            <Box
                                sx={{
                                    width: '80px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: 0.5,
                                    color: isPositive ? '#10b981' : '#ef4444',
                                }}
                            >
                                {isPositive ? (
                                    <Iconify icon="eva:trending-up-fill" width={18} height={18} />
                                ) : (
                                    <Iconify icon="eva:trending-down-fill" width={18} height={18} />
                                )}
                                <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>
                                    {isPositive ? '+' : ''}{item.change?.toFixed(1)}%
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {data.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No comparison data available
                    </Typography>
                </Box>
            )}

            {/* Legend */}
            <Box
                sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    gap: 3,
                    justifyContent: 'center',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 2, bgcolor: '#3b82f6' }} />
                    <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Current Period</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 2, bgcolor: '#94a3b8' }} />
                    <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Previous Period</Typography>
                </Box>
            </Box>
        </Box>
    );
}

YearOverYearChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            metric: PropTypes.string.isRequired,
            current: PropTypes.number.isRequired,
            previous: PropTypes.number.isRequired,
            change: PropTypes.number.isRequired,
        })
    ).isRequired,
    title: PropTypes.string,
};

YearOverYearChart.defaultProps = {
    title: 'Year Over Year Comparison',
};
